import React, { useState } from 'react';
import { X, CheckCircle, Warning } from '@phosphor-icons/react';
import { AnalyzerWeeklyPrompt } from '@Models/types';

interface SetDefaultConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (prompt: AnalyzerWeeklyPrompt) => void;
  prompt: AnalyzerWeeklyPrompt | null;
  currentDefaultPrompt?: AnalyzerWeeklyPrompt | null;
}

export const SetDefaultConfirmationModal: React.FC<
  SetDefaultConfirmationModalProps
> = ({ isOpen, onClose, onConfirm, prompt, currentDefaultPrompt }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!prompt) return;

    setLoading(true);
    try {
      await onConfirm(prompt);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !prompt) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
              <Warning size={20} className="text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Set Default Prompt
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-4">
          <div className="text-sm text-gray-600">
            <p className="mb-3">
              Are you sure you want to set{' '}
              <span className="font-medium text-gray-900">
                "{prompt.promptTitle}"
              </span>{' '}
              as the default prompt?
            </p>

            {currentDefaultPrompt && (
              <div className="rounded-md bg-yellow-50 p-3">
                <p className="text-yellow-800">
                  <span className="font-medium">Current default:</span> "
                  {currentDefaultPrompt.promptTitle}" will no longer be the
                  default prompt.
                </p>
              </div>
            )}

            <p className="mt-3 text-xs text-gray-500">
              The default prompt is used for all analyzer weekly
              classifications.
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Setting...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                Set as Default
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
